import { type FC, type ReactNode, useLayoutEffect, useRef } from 'react';
import { RiCheckboxCircleFill, RiErrorWarningLine, RiLoader4Line } from 'react-icons/ri';
import { _isEmpty, _isNil, _notNil } from '@/littledash';
import type { ActivityRow } from './ActivityRow.utils';
import styles from './ActivityPanelContent.module.scss';

interface ActivityPanelContentProps {
  rows: ReadonlyArray<ActivityRow>;
}

// Treat the list as "at the bottom" within this many px of the end, so sub-pixel rounding or a
// partially-visible final row still counts as following the tail.
const AT_BOTTOM_THRESHOLD_PX = 24;

/**
 * The status icon for a row: a green filled check for a completed phase/tool call, a neutral "ended"
 * icon when the session resolved with it still open, and a blue spinner for anything still in progress
 * (a running phase or tool call, or thinking — which has no dedicated done signal).
 */
const statusIcon = (row: ActivityRow): ReactNode => {
  const state = row.kind === 'thinking' ? undefined : row.state;
  if (state === 'done') {
    return <RiCheckboxCircleFill className={styles.tick} aria-label="Done" />;
  }
  if (state === 'resolved') {
    return <RiErrorWarningLine className={styles.resolved} aria-label="Ended" />;
  }
  return <RiLoader4Line className={styles.spinner} aria-label="Running" />;
};

/** The primary text for a row: thinking text, phase label, or tool name. */
const rowTitle = (row: ActivityRow): string => {
  switch (row.kind) {
    case 'thinking':
      return row.text;
    case 'phase':
      return row.label;
    case 'tool':
      return row.toolName;
  }
};

/**
 * Pure renderer of the derived Activity rows as a scrollable list of event cards: a status icon plus
 * a bold title (the tool name, or the thinking text). Holds no store or derivation — it renders
 * exactly the rows it is given.
 */
const ActivityPanelContent: FC<ActivityPanelContentProps> = ({ rows }) => {
  const listRef = useRef<HTMLUListElement>(null);
  // Whether the list was scrolled to (near) the bottom before the latest render. Updated on scroll so
  // the layout effect below knows whether to follow the tail. Starts true so the first events stick.
  const atBottomRef = useRef(true);

  const handleScroll = (): void => {
    const list = listRef.current;
    if (_isNil(list)) {
      return;
    }
    atBottomRef.current = list.scrollHeight - list.scrollTop - list.clientHeight <= AT_BOTTOM_THRESHOLD_PX;
  };

  // After rows change, follow the tail ONLY if the user was already at the bottom — so a live feed
  // auto-scrolls, but a user who scrolled up to read history is left in place. useLayoutEffect so the
  // scroll is applied before paint (no flash of the pre-scroll position).
  useLayoutEffect(() => {
    const list = listRef.current;
    if (_notNil(list) && atBottomRef.current) {
      list.scrollTop = list.scrollHeight;
    }
  }, [rows]);

  if (_isEmpty(rows)) {
    return (
      <p className={styles.empty} data-test-element="empty">
        No activity yet.
      </p>
    );
  }

  return (
    <ul ref={listRef} onScroll={handleScroll} className={styles.list} data-test-element="activity-list">
      {rows.map((row) => (
        <li
          key={row.key}
          className={styles.card}
          data-test-element="activity-row"
          data-kind={row.kind}
          data-state={row.kind === 'thinking' ? undefined : row.state}
        >
          <span className={styles.icon}>{statusIcon(row)}</span>
          <span className={styles.title}>{rowTitle(row)}</span>
        </li>
      ))}
    </ul>
  );
};

export default ActivityPanelContent;
